import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./childlabor.css";
import AudioFloat from "./AudioFloat.js";

export default function Childlabor({ fetchPhotographs, photographs }) {
  /* ---------- Mount behavior ---------- */
  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => { if (!photographs) fetchPhotographs?.(); }, [photographs, fetchPhotographs]);

  /* ---------- Normalize, filter, sort ---------- */
  const items = useMemo(() => {
    if (!photographs) return [];
    const arr = Object.keys(photographs).map((k) => {
      const p = photographs[k];
      if (!p) return null;
      const src = p.imageURL || p.url;
      if (!src) return null;

      const rawOrder = (p.order && (p.order.$numberInt ?? p.order.$numberLong)) ?? p.order;
      const order = typeof rawOrder === "string" ? parseInt(rawOrder, 10) : (rawOrder ?? 999999);

      const full =
        !!p.full || !!p.fullWidth || !!p.fullwidth || !!p.full_width;

      return {
        ...p,
        __src: src,
        __order: Number.isFinite(order) ? order : 999999,
        __full: full
      };
    })
    .filter(Boolean)
    .filter((p) => p.childlabor && p.__src);

    arr.sort((a, b) => (a.__order - b.__order) || String(a.__src).localeCompare(String(b.__src)));
    return arr;
  }, [photographs]);

  /* ---------- Hero: prefer hero:true else random ---------- */
  const [hero, setHero] = useState(null);
  useEffect(() => {
    if (!items.length) return;
    const flagged = items.find((x) => x.hero);
    setHero(flagged || items[Math.floor(Math.random() * items.length)]);
  }, [items]);

  /* ---------- Lightbox ---------- */
  const [lbOpen, setLbOpen] = useState(false);
  const [lbIndex, setLbIndex] = useState(0);

  const openAt = (idx) => { setLbIndex(idx); setLbOpen(true); };
  const onClose = () => setLbOpen(false);
  const onPrev  = () => setLbIndex((i) => (i - 1 + items.length) % items.length);
  const onNext  = () => setLbIndex((i) => (i + 1) % items.length);

  const onKey = useCallback((e) => {
    if (!lbOpen) return;
    if (e.key === "Escape") onClose();
    if (e.key === "ArrowLeft") onPrev();
    if (e.key === "ArrowRight") onNext();
  }, [lbOpen]);

  useEffect(() => {
    if (lbOpen) document.documentElement.classList.add("no-scroll");
    else document.documentElement.classList.remove("no-scroll");

    window.addEventListener("keydown", onKey);
    return () => {
      document.documentElement.classList.remove("no-scroll");
      window.removeEventListener("keydown", onKey);
    };
  }, [lbOpen, onKey]);

  /* ---------- Scroll-to-top visibility ---------- */
  const [showTop, setShowTop] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY || document.documentElement.scrollTop;
      setShowTop(y > 480); // show after ~480px
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToTop = () => {
    const prefersReduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (prefersReduced) window.scrollTo(0, 0);
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /* ---------- Grid card (3-up or full-width row) ---------- */
  const Card = ({ item, index }) => {
    const [loaded, setLoaded] = useState(false);
    return (
      <figure
        className={`card ${item.__full ? "full" : ""}`}
        onClick={() => openAt(index)}
        role="button"
        aria-label="Open photo"
      >
        <img
          src={item.__src}
          alt={item.title || "Child labor photograph"}
          loading="lazy"
          decoding="async"
          className={loaded ? "loaded" : ""}
          onLoad={() => setLoaded(true)}
        />
      </figure>
    );
  };

  return (
    <main className="page childlabor-page" data-light-nav="true">
      {/* HERO */}
      <header className="hero" style={{ minHeight: "90vh" }}>
        {hero && (
          <img
            className="hero-img kenburns-top"
            src={hero.__src}
            alt="Child labor hero"
            loading="eager"
            decoding="async"
          />
        )}
        <div className="nameplate">
          <h1 className="hed">A STORY THAT MUST BE TOLD</h1>
        <p className="dek">
            A visual report on childhood interrupted—work, risk, and resilience across borders.
          </p>
        </div>

        {/* SCROLL CUE (bouncing arrow like fashion) */}
        <button
          className="hero-scroll-cue"
          onClick={() =>
            document.querySelector(".wrap")?.scrollIntoView({ behavior: "smooth" })
          }
          aria-label="Scroll to content"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 16l-6-6h12l-6 6z" fill="currentColor" />
          </svg>
        </button>
      </header>

      <section className="wrap">
        {/* COPY A */}
        <article className="article article-tight">
          <p className="dropcap">
            One of the reasons I decided to pick up a camera and travel the world is because of my
            deep emotional connection to people. I’ve always felt others’ pain as if it were my own.
            Photography became my way to help — to bring awareness, to make people smile, and to
            show the truth of what I see.
          </p>
          <aside className="pullquote in-text">
            <q>I’ve always felt others’ pain as if it were my own.</q>
          </aside>
          <p>
            During my travels, I’ve witnessed many stories — some filled with beauty and joy, others
            marked by struggle. Child labor is one of the most heartbreaking realities to see.
          </p>
          <aside className="pullquote in-text">
            <q>Children work instead of learning, instead of dreaming.</q>
          </aside>
        </article>

        {/* GALLERY A — strict 3-up or full-width rows */}
        <section className="gallery">
          {items.slice(0, 14).map((item, i) => (
            <Card key={`${item.__src}-${i}`} item={item} index={i} />
          ))}
        </section>

        {/* COPY B */}
        <article className="article article-tight">
          <p>
            It’s painful to witness, but it’s also a reminder of how powerful the human spirit can be.
            Through my photography, I want to shed light on these unseen stories — not to exploit pain,
            but to awaken empathy.
          </p>
          <aside className="pullquote in-text">
            <q>Not to exploit pain, but to awaken empathy.</q>
          </aside>
          <p>
            My hope is for a future where no child has to work to survive — where every child can
            play, learn, and grow freely.
          </p>
          <aside className="pullquote in-text">
            <q>A world where childhood is protected.</q>
          </aside>
        </article>

        {/* GALLERY B — strict 3-up or full-width rows */}
        <section className="gallery">
          {items.slice(14).map((item, i) => (
            <Card key={`${item.__src}-b-${i}`} item={item} index={i + 14} />
          ))}
        </section>

        {/* CTA */}
        <footer className="kicker">
          <p>
            Awareness is the start — action is the change. Support causes that advocate for
            children’s rights and education in your region.
          </p>
          <div className="links">
            <Link className="btn-inline" to="/humanitarianism/causes">
              See How You Can Help →
            </Link>
          </div>
        </footer>
      </section>

      {/* SCROLL-TO-TOP (hidden near top; shows after scrolling) */}
      {!lbOpen && (
        <button
          className={`scroll-top ${showTop ? "is-visible" : ""}`}
          onClick={scrollToTop}
          aria-label="Scroll to top"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 8l6 6H6l6-6z" fill="currentColor" />
          </svg>
        </button>
      )}

      {/* LIGHTBOX — frosted white overlay + darker round pills */}
      {lbOpen && items[lbIndex] && (
        <div
          className="lb lightbox"
          role="dialog"
          aria-modal="true"
          onClick={onClose}
        >
          <button
            className="lb-close"
            aria-label="Close"
            onClick={(e) => { e.stopPropagation(); onClose(); }}
          >
            ×
          </button>

          <button
            className="lb-nav left"
            aria-label="Previous"
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
          >
            ‹
          </button>

          <div className="lb-stage" onClick={(e) => e.stopPropagation()}>
            <img
              className="lb-img loaded"
              src={items[lbIndex].__src}
              alt={items[lbIndex].title || "Child labor photograph large view"}
            />
          </div>

          <button
            className="lb-nav right"
            aria-label="Next"
            onClick={(e) => { e.stopPropagation(); onNext(); }}
          >
            ›
          </button>
        </div>
      )}

              <AudioFloat
                    srcs={[
                      { src: "https://lvltravel.s3.us-east-2.amazonaws.com/Music/causes.mp3", type: "audio/mpeg" },
                      
                    ]}
                    startMuted={false}
                    startVolume={0.5}
                    loop={true}
                    label="Site background audio"
                    startTime = {20}
                    
                  />
    </main>
  );
}
